import { create } from "zustand";
import { persist } from "zustand/middleware";

export type SectionState = "pending" | "active" | "completed" | "invalidated";

export interface ToolExecution {
  id: string;
  timestamp: string;
  parameters: Record<string, unknown>;
  result?: unknown;
  error?: string;
  status: "pending" | "success" | "error";
  duration?: number;
}

export interface ToolState {
  parameters: Record<string, string>;
  paramErrors: Record<string, string | undefined>;
  lastResponse: unknown | null;
  lastError: string | null;
  executionHistory: ToolExecution[];
  isExecuting: boolean;
}

export interface PromptExecution {
  id: string;
  timestamp: string;
  arguments: Record<string, unknown>;
  result?: unknown;
  error?: string;
  status: "pending" | "success" | "error";
  duration?: number;
}

export interface PromptState {
  arguments: Record<string, string>;
  argumentErrors: Record<string, string | undefined>;
  lastResponse: unknown | null;
  lastError: string | null;
  executionHistory: PromptExecution[];
  isExecuting: boolean;
}

export interface InspectorStore {
  // Timeline state
  selectedTool: string | null;
  selectedPrompt: string | null;
  selectSectionState: SectionState;
  configureSectionState: SectionState;

  // Per-tool state persistence
  toolStates: Record<string, ToolState>;
  promptStates: Record<string, PromptState>;

  // Actions
  setSelectedTool: (toolName: string | null) => void;
  setSelectedPrompt: (promptName: string | null) => void;
  getSectionState: (section: "select" | "configure") => SectionState;
  invalidateConfigureSection: () => void;

  // Tool state management
  getToolState: (toolName: string) => ToolState;
  updateToolParameters: (
    toolName: string,
    parameters: Record<string, string>,
  ) => void;
  updateToolParamErrors: (
    toolName: string,
    errors: Record<string, string | undefined>,
  ) => void;
  setToolExecuting: (toolName: string, executing: boolean) => void;
  setToolResponse: (toolName: string, response: unknown) => void;
  setToolError: (toolName: string, error: string | null) => void;
  addToolExecution: (toolName: string, execution: ToolExecution) => void;
  resetTool: (toolName: string) => void;

  // Prompt state management
  getPromptState: (promptName: string) => PromptState;
  updatePromptArguments: (
    promptName: string,
    args: Record<string, string>,
  ) => void;
  updatePromptArgErrors: (
    promptName: string,
    errors: Record<string, string | undefined>,
  ) => void;
  setPromptExecuting: (promptName: string, executing: boolean) => void;
  setPromptResponse: (promptName: string, response: unknown) => void;
  setPromptError: (promptName: string, error: string | null) => void;
  addPromptExecution: (promptName: string, execution: PromptExecution) => void;
  resetPrompt: (promptName: string) => void;
  resetAllState: () => void;
}

const defaultToolState: ToolState = {
  parameters: {},
  paramErrors: {},
  lastResponse: null,
  lastError: null,
  executionHistory: [],
  isExecuting: false,
};

const defaultPromptState: PromptState = {
  arguments: {},
  argumentErrors: {},
  lastResponse: null,
  lastError: null,
  executionHistory: [],
  isExecuting: false,
};

export const useInspectorStore = create<InspectorStore>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedTool: null,
      selectedPrompt: null,
      selectSectionState: "active",
      configureSectionState: "pending",
      toolStates: {},
      promptStates: {},

      // Actions
      setSelectedTool: (toolName) => {
        const state = get();

        if (toolName) {
          // Get the new tool's state to determine configure section state
          const newToolState = state.toolStates[toolName];
          const hasResults =
            newToolState?.lastResponse !== null ||
            newToolState?.lastError !== null;

          let configureSectionState: SectionState = "active";

          if (hasResults && newToolState?.executionHistory?.length > 0) {
            // Compare current parameters with the parameters from the most recent execution
            const lastExecution = newToolState.executionHistory[0]; // Most recent is first
            const currentParams = newToolState.parameters;
            const lastExecutionParams = lastExecution.parameters;

            // Deep comparison of parameters
            const paramsMatch =
              JSON.stringify(currentParams) ===
              JSON.stringify(lastExecutionParams);

            if (paramsMatch) {
              configureSectionState = "completed";
            } else {
              configureSectionState = "invalidated";
            }
          }

          set({
            selectedTool: toolName,
            selectSectionState: "completed",
            configureSectionState,
          });
        } else {
          set({
            selectedTool: null,
            selectSectionState: "active",
            configureSectionState: "pending",
          });
        }
      },

      setSelectedPrompt: (promptName) => {
        if (promptName) {
          const state = get();
          const newPromptState = state.promptStates[promptName];
          const hasResults =
            newPromptState?.lastResponse !== null ||
            newPromptState?.lastError !== null;

          let configureSectionState: SectionState = "active";

          if (hasResults && newPromptState?.executionHistory?.length > 0) {
            // Compare current parameters with the parameters from the most recent execution
            const lastExecution = newPromptState.executionHistory[0]; // Most recent is first
            const currentArgs = newPromptState.arguments;
            const lastExecutionArgs = lastExecution.arguments;

            // Deep comparison of arguments
            const argsMatch =
              JSON.stringify(currentArgs) === JSON.stringify(lastExecutionArgs);

            if (argsMatch) {
              configureSectionState = "completed";
            } else {
              configureSectionState = "invalidated";
            }
          }

          set({
            selectedPrompt: promptName,
            selectSectionState: "completed",
            configureSectionState,
          });
        } else {
          set({
            selectedPrompt: null,
            selectSectionState: "active",
            configureSectionState: "pending",
          });
        }
      },

      getSectionState: (section) => {
        const state = get();
        if (section === "select") {
          return state.selectSectionState;
        }
        return state.configureSectionState;
      },

      invalidateConfigureSection: () => {
        set({ configureSectionState: "invalidated" });
      },

      // Tool state management
      getToolState: (toolName) => {
        const state = get();
        return state.toolStates[toolName] || defaultToolState;
      },

      updateToolParameters: (toolName, parameters) =>
        set((state) => ({
          toolStates: {
            ...state.toolStates,
            [toolName]: {
              ...(state.toolStates[toolName] || defaultToolState),
              parameters,
            },
          },
        })),

      updateToolParamErrors: (toolName, errors) =>
        set((state) => ({
          toolStates: {
            ...state.toolStates,
            [toolName]: {
              ...(state.toolStates[toolName] || defaultToolState),
              paramErrors: errors,
            },
          },
        })),

      setToolExecuting: (toolName, executing) =>
        set((state) => ({
          toolStates: {
            ...state.toolStates,
            [toolName]: {
              ...(state.toolStates[toolName] || defaultToolState),
              isExecuting: executing,
            },
          },
        })),

      setToolResponse: (toolName, response) =>
        set((state) => ({
          toolStates: {
            ...state.toolStates,
            [toolName]: {
              ...(state.toolStates[toolName] || defaultToolState),
              lastResponse: response,
              lastError: null,
              isExecuting: false,
            },
          },
          // Mark configure section as completed when we have a response
          configureSectionState:
            response !== null && state.selectedTool === toolName
              ? "completed"
              : state.configureSectionState,
        })),

      setToolError: (toolName, error) =>
        set((state) => ({
          toolStates: {
            ...state.toolStates,
            [toolName]: {
              ...(state.toolStates[toolName] || defaultToolState),
              lastError: error,
              lastResponse: null,
              isExecuting: false,
            },
          },
          // Mark configure section as completed even with error (user has executed)
          configureSectionState:
            error && state.selectedTool === toolName
              ? "completed"
              : state.configureSectionState,
        })),

      addToolExecution: (toolName, execution) =>
        set((state) => ({
          toolStates: {
            ...state.toolStates,
            [toolName]: {
              ...(state.toolStates[toolName] || defaultToolState),
              executionHistory: [
                execution,
                ...((state.toolStates[toolName] || defaultToolState)
                  .executionHistory || []),
              ].slice(0, 50), // Keep last 50 executions
            },
          },
        })),

      resetTool: (toolName) =>
        set((state) => ({
          toolStates: {
            ...state.toolStates,
            [toolName]: defaultToolState,
          },
          // Reset configure section to active if this is the selected tool
          configureSectionState:
            state.selectedTool === toolName
              ? "active"
              : state.configureSectionState,
        })),

      getPromptState: (promptName) => {
        const state = get();
        return state.promptStates[promptName] || defaultPromptState;
      },

      updatePromptArguments: (promptName, args) =>
        set((state) => ({
          promptStates: {
            ...state.promptStates,
            [promptName]: {
              ...(state.promptStates[promptName] || defaultPromptState),
              arguments: args,
            },
          },
        })),

      updatePromptArgErrors: (promptName, errors) =>
        set((state) => ({
          promptStates: {
            ...state.promptStates,
            [promptName]: {
              ...(state.promptStates[promptName] || defaultPromptState),
              argumentErrors: errors,
            },
          },
        })),

      setPromptExecuting: (promptName, executing) =>
        set((state) => ({
          promptStates: {
            ...state.promptStates,
            [promptName]: {
              ...(state.promptStates[promptName] || defaultPromptState),
              isExecuting: executing,
            },
          },
        })),

      setPromptResponse: (promptName, response) =>
        set((state) => ({
          promptStates: {
            ...state.promptStates,
            [promptName]: {
              ...(state.promptStates[promptName] || defaultPromptState),
              lastResponse: response,
              lastError: null,
              isExecuting: false,
            },
          },
        })),

      setPromptError: (promptName, error) =>
        set((state) => ({
          promptStates: {
            ...state.promptStates,
            [promptName]: {
              ...(state.promptStates[promptName] || defaultPromptState),
              lastError: error,
              lastResponse: null,
              isExecuting: false,
            },
          },
        })),

      addPromptExecution: (promptName, execution) =>
        set((state) => ({
          promptStates: {
            ...state.promptStates,
            [promptName]: {
              ...(state.promptStates[promptName] || defaultPromptState),
              executionHistory: [
                execution,
                ...((state.promptStates[promptName] || defaultPromptState)
                  .executionHistory || []),
              ].slice(0, 50),
            },
          },
        })),

      resetPrompt: (promptName) =>
        set((state) => ({
          promptStates: {
            ...state.promptStates,
            [promptName]: defaultPromptState,
          },
        })),

      resetAllState: () =>
        set({
          selectedTool: null,
          selectedPrompt: null,
          selectSectionState: "active",
          configureSectionState: "pending",
          toolStates: {},
          promptStates: {},
        }),
    }),
    {
      name: "inspector-store",
      // Only persist tool states, not section states/selection (those should reset on page load)
      partialize: (state) => ({
        toolStates: state.toolStates,
        promptStates: state.promptStates,
      }),
    },
  ),
);
